<template>
  <div class="chat-input">
    <!-- <ChatTip :aciveState="!isChatHistoryEmpty" /> -->
    <div class="chat-input-box">
      <LargeTextInput @input-change="onInputChange" @submit="onSendMessage" :inputValue="text" />

      <SubmitMessageButton
        class="chat-submit start-over"
        :class="{inactive: !isClearBtnActive}"
        text="Start Over"
        @submit="onClearAll"
      />

      <SubmitMessageButton
        class="chat-submit"
        text="Generate Preferences"
        :isLoading="isLoading"
        @submit="onSendMessage"
        :disabled="!isActive"
      />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import Toasted from 'vue-toasted';
import LargeTextInput from '@common/imput/large-text-input.vue';
import SubmitMessageButton from '@common/button/submit-message-button.vue';
// import ChatTip from '@common/chat/chat-tip.vue';
import {ChatHistoryModule} from '@/store/modules';
import {getDialogResponse} from '@/store/api/get-dialog-response';
import {DomainSection} from '@/store/state/utils';
import {
  dropManualActivities,
  dropSolution,
  getAllSelectedFilters,
  getManualActivities,
  getSolution,
  updateSolutionFilters,
} from '@/store/state/helpers';

Vue.use(Toasted);

const defaultErrMessage =
  'Sorry we could not understand this input about your trip to Singapore. Could you try again with a different description of your preferences?';

export default Vue.extend({
  name: 'ChatTextInput',
  components: {LargeTextInput, SubmitMessageButton},
  props: {
    allFilters: {
      type: Array,
      default: () => [],
    },
    pillsValue: {
      type: String,
      default: () => '',
    },
  },

  data() {
    return {
      text: this.pillsValue,
      isActive: false,
      isLoading: false,
    };
  },

  computed: {
    isChatHistoryEmpty() {
      return ChatHistoryModule.getMessages().length === 0;
    },
    isClearBtnActive() {
      const isSolutionAvailable = !!getSolution();
      const messages = ChatHistoryModule.getMessages();
      const manualActivities = getManualActivities();
      const selectedFilters = getAllSelectedFilters();
      return (
        isSolutionAvailable ||
        this.text ||
        messages.length > 0 ||
        manualActivities.length > 0 ||
        selectedFilters.length > 0
      );
    },
  },
  watch: {
    pillsValue(val) {
      this.text = val;
    },
  },
  methods: {
    onInputChange(val) {
      this.text = val;
      if (val.length > 0) {
        this.isActive = true;
      } else {
        this.isActive = false;
      }
    },
    // TODO: refactor this: remove to some helper file
    showErrorToast(message = defaultErrMessage) {
      Vue.toasted.show(message, {
        position: 'top-right',
        className: 'toast-error',
        duration: 5000,
        action: {
          text: 'CLOSE',
          onClick: (_event, toastObject) => {
            toastObject.goAway(0);
          },
        },
        singleton: true,
      });
    },

    onClearAll() {
      ChatHistoryModule.clearMessages();
      updateSolutionFilters([]);
      dropManualActivities();
      dropSolution();
      this.text = '';
      this.$emit('clearPill');
    },

    async onSendMessage() {
      if (!this.text || this.isLoading) return;
      this.isLoading = true;

      // add message to chat history
      const message = this.text;
      ChatHistoryModule.addMessage(message);

      // clear text field
      this.text = '';

      // send request and wait response
      const response = await getDialogResponse(message, 'Singapore');
      this.isLoading = false;

      // if no response — show error toast
      if (!response) {
        this.showErrorToast();
        ChatHistoryModule.removeLastMessage();
        return;
      }

      // if AI can't parse text - show error toast
      if (!response.success) {
        this.showErrorToast();
        ChatHistoryModule.removeLastMessage();
      } else {
        const recommendedFilters = response?.recommended_filters || [];
        const activatedFilterIds = new Set(recommendedFilters.map((x) => x.id));
        const filterIds = new Set(this.allFilters.map((x) => x.id));
        const missingFilters = recommendedFilters
          .filter((x) => !filterIds.has(x.id))
          .map((x) => ({
            id: x.id,
            domain: x.category,
            domainSection: DomainSection.PLACES,
            parent: x.parent,
            name: x.name,
            value: true,
          }));

        const activatedFilters = this.allFilters
          .filter((x) => activatedFilterIds.has(x.id))

          .map((x) => ({...x, value: true}))
          .concat(missingFilters)
          .filter((f) => f.domain !== 'GEOLOCATION')
          .sort((a, b) => [...activatedFilterIds].indexOf(a.id) - [...activatedFilterIds].indexOf(b.id));

        if (activatedFilters.length) {
          this.$emit('update', activatedFilters);
        }
      }

      // Update dates from response
      if (response.trip_start_time && response.trip_end_time) {
        // adding to date time part(12:00 UTC), so in all timezones we have same date.
        const dateInfo = {
          startDate: new Date(response.trip_start_date + 'T12:00Z'), // make date from ISO date string YYYY-MM-DD
          endDate: new Date(response.trip_end_date + 'T12:00Z'), // make date from ISO date string YYYY-MM-DD
        };

        this.$emit('update-dates', dateInfo);
      }

      // Update travelers and budget from response
      if (response.traveler_information) {
        this.$emit('update-travelers-and-budget', {
          travelersInformation: response.traveler_information,
          budget: response.budget?.price || null,
        });
      }
    },
  },
});
</script>

<style scoped lang="scss">
.chat-input-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
}
</style>
